import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, ChevronRight, ListChecks } from "lucide-react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { UserAvatar } from "../components/ui/user-avatar";
import { EmptyState } from "../components/ui/empty-state";
import { useTasks, usePendingBranchAccessRequests } from "../hooks/queries";
import {
  Task, listEmailJobs, approveEmailJob, grantBranchAccessRequest, denyBranchAccessRequest,
} from "../api/api";
import { taskTarget } from "../lib/swipe";
import { toast } from "../lib/toast";

const INLINE_LIMIT = 5;

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return "zojuist";
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? "gisteren" : `${d} dagen geleden`;
}

function GroupHeader({ title, count, linkLabel, onLink }: { title: string; count?: number; linkLabel?: string; onLink?: () => void }) {
  return (
    <div className="flex items-center gap-3">
      <h2 className="text-lg font-black tracking-[-0.01em]">{title}</h2>
      {count != null && (
        <span className="rounded-pill bg-waves-mist px-2.5 py-0.5 font-mono text-xs font-semibold">{count}</span>
      )}
      {linkLabel && onLink && (
        <button onClick={onLink} className="ml-auto text-[13px] font-bold hover:text-waves-accent">{linkLabel} →</button>
      )}
    </div>
  );
}

function TaskRow({ icon, title, meta, children, onClick }: { icon: React.ReactNode; title: React.ReactNode; meta?: React.ReactNode; children?: React.ReactNode; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? e => { if (e.key === "Enter") onClick(); } : undefined}
      className={`flex flex-wrap items-center gap-4 rounded-[16px] border border-waves-line bg-white px-5 py-4 ${onClick ? "cursor-pointer transition-colors hover:bg-waves-subtle" : ""}`}
    >
      {icon}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="text-[15px] font-bold">{title}</div>
        {meta && <div className="text-[13px] text-waves-muted">{meta}</div>}
      </div>
      {children && <div className="flex shrink-0 items-center gap-2">{children}</div>}
    </div>
  );
}

function AllDone({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[16px] border border-dashed border-waves-line px-5 py-[18px] text-sm font-bold text-status-ok-fg">✓ {children}</div>
  );
}

export default function Taken() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useTasks();
  // Every task type is grouped server-side (at most one Task per type), so
  // task.type is already a unique, stable key — no per-item grouping needed.
  const tasks: Task[] = data?.tasks ?? [];
  const hasMailTask = tasks.some(t => t.type === "email_approval");
  const hasAccessTask = tasks.some(t => t.type === "branch_access_request");
  const otherTasks = tasks.filter(t => t.type !== "email_approval" && t.type !== "branch_access_request");

  // The inline lists are only fetched when the matching task exists, which
  // also means the user is allowed to see them.
  const { data: mails } = useQuery({
    queryKey: ["admin", "emails", "taken-inline"],
    queryFn: () => listEmailJobs(1, INLINE_LIMIT, { approvalStatus: "pending" }),
    enabled: hasMailTask,
  });
  const { data: accessRequests = [] } = usePendingBranchAccessRequests(hasAccessTask);

  function refreshTasks() {
    qc.invalidateQueries({ queryKey: ["tasks"] });
  }

  const approveMail = useMutation({
    mutationFn: (id: number) => approveEmailJob(id),
    onSuccess: () => toast("Mail goedgekeurd", { kind: "success" }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["admin", "emails"] }); refreshTasks(); },
  });
  const grant = useMutation({
    mutationFn: (id: number) => grantBranchAccessRequest(id),
    onSuccess: () => toast("Toegang gegeven", { kind: "success" }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["branch-requests"] }); refreshTasks(); },
  });
  const deny = useMutation({
    mutationFn: (id: number) => denyBranchAccessRequest(id),
    onSuccess: () => toast("Verzoek afgewezen", { kind: "success" }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["branch-requests"] }); refreshTasks(); },
  });
  const accessBusy = grant.isPending || deny.isPending;

  async function openTask(task: Task) {
    const target = await taskTarget(task);
    if (target) navigate(target.to, { state: target.state });
  }

  return (
    <Layout title="Taken" subtitle={tasks.length > 0 ? `${tasks.length} open · alles wat op jouw actie wacht` : "Alles afgehandeld"}>
      <div className="mx-auto flex max-w-[1000px] flex-col gap-8">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-waves-muted">Laden…</div>
        ) : tasks.length === 0 ? (
          <EmptyState icon={<ListChecks />} title="Geen openstaande taken" description="Alles is afgehandeld. Nieuwe taken verschijnen hier vanzelf." className="rounded-[16px] border border-waves-line" />
        ) : (
          <>
            {hasMailTask && (
              <section className="flex flex-col gap-3">
                <GroupHeader title="Mails ter goedkeuring" count={mails?.total} linkLabel="Alle mails" onLink={() => navigate("/mail-goedkeuring")} />
                {(mails?.data ?? []).map(m => (
                  <TaskRow
                    key={m.id}
                    icon={<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-nav border border-waves-line bg-waves-subtle"><Mail size={18} /></div>}
                    title={m.recipient_name || m.recipient_email}
                    meta={`${m.email_type.replace(/_/g, " ")} · ${timeAgo(m.created_at)}`}
                  >
                    <Button variant="outline" size="sm" onClick={() => navigate("/mail-goedkeuring")}>Bekijk</Button>
                    <Button variant="ink" size="sm" onClick={() => approveMail.mutate(m.id)} disabled={approveMail.isPending}>Goedkeuren</Button>
                  </TaskRow>
                ))}
                {mails && mails.data.length === 0 && <AllDone>Geen mails meer te beoordelen</AllDone>}
              </section>
            )}

            {hasAccessTask && (
              <section className="flex flex-col gap-3">
                <GroupHeader title="Toegangsverzoeken" count={accessRequests.length} linkLabel="Alle verzoeken" onLink={() => navigate("/toegangsverzoeken")} />
                {accessRequests.slice(0, INLINE_LIMIT).map(r => (
                  <TaskRow
                    key={r.id}
                    icon={<UserAvatar name={r.user_name} size={40} />}
                    title={<>{r.user_name} vraagt toegang tot {r.branch_name}</>}
                    meta={`${r.user_email} · ${timeAgo(r.created_at)}`}
                  >
                    <Button variant="destructive" size="sm" onClick={() => deny.mutate(r.id)} disabled={accessBusy}>Afwijzen</Button>
                    <Button variant="ink" size="sm" onClick={() => grant.mutate(r.id)} disabled={accessBusy}>Toegang geven</Button>
                  </TaskRow>
                ))}
                {accessRequests.length === 0 && <AllDone>Geen open toegangsverzoeken</AllDone>}
              </section>
            )}

            {otherTasks.length > 0 && (
              <section className="flex flex-col gap-3">
                <GroupHeader title="Overige taken" count={otherTasks.length} />
                {otherTasks.map(task => (
                  <TaskRow
                    key={task.type}
                    icon={<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-nav bg-waves-mist"><ListChecks size={18} /></div>}
                    title={task.title || task.message}
                    meta={task.title ? `${task.message} · ${timeAgo(task.created_at)}` : timeAgo(task.created_at)}
                    onClick={() => void openTask(task)}
                  >
                    <ChevronRight size={18} className="text-waves-muted" />
                  </TaskRow>
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
